import { useEffect, useMemo } from 'react'
import {
  SwitcherOrg,
  SwitcherTeam,
  useOrgTeamTree
} from 'components/DashboardBanners/OrgTeamSwitcher/use-org-team-tree'
import { useStores } from 'hooks/useStores'

export interface UseContextSwitcherResult {
  /** Organizations the user belongs to. */
  organizations: SwitcherOrg[]
  /** Teams of the selected organization only — never another org's. */
  teams: SwitcherTeam[]
  org?: SwitcherOrg
  team?: SwitcherTeam
  setOrg: (orgId: number) => void
  /** `null` selects the aggregate across the organization's Teams. */
  setTeam: (teamId: number | null) => void
  isLoading: boolean
}

/**
 * The Organization → Team context shared by the pages that show a context banner.
 *
 * One source for both levels: `useOrgTeamTree` reads `GET /users/me/teams`, which
 * returns a row per organization-and-team, so the org list and each org's Team
 * list arrive together. Asking `GET /teams` once per organization instead — which
 * is what the Schedule switcher used to do — costs one request per organization
 * and cannot answer "which orgs do I belong to" at all.
 *
 * Selection is the global one (`authStore`), so the context survives navigation
 * between pages of the same level, which is what the ticket asks for.
 *
 * @param withTeam pages that are Team-scoped (Schedule, Assignments). Org-level
 * pages leave it off and never touch the Team context — landing on Plans must not
 * silently pick a Team for the pages that do care.
 */
export const useContextSwitcher = (withTeam = false): UseContextSwitcherResult => {
  const { authStore } = useStores()
  const { organizations, teamsByOrg, isLoading } = useOrgTeamTree()

  const org = useMemo(
    () => organizations.find(candidate => candidate.id === authStore.activeOrgId),
    [organizations, authStore.activeOrgId]
  )

  const teams = useMemo(
    () => (authStore.activeOrgId ? (teamsByOrg.get(authStore.activeOrgId) ?? []) : []),
    [teamsByOrg, authStore.activeOrgId]
  )

  const team = useMemo(
    () => teams.find(candidate => candidate.id === authStore.activeTeamId),
    [teams, authStore.activeTeamId]
  )

  // Land on "All teams" rather than on nothing — but only when nothing has been
  // chosen. An explicit "All teams" is already that, so it is left alone;
  // otherwise walking from Schedule to Assignments would reset the choice.
  //
  // Defaulting to the aggregate rather than to `teams[0]`: picking one Team for
  // the user hides the rest of their schedule behind a filter they never set.
  //
  // This is also the correction path: a Team stored from a previous session may
  // belong to another organization, and `teams` is what decides whether it still
  // applies here — when it does not, fall back to the aggregate.
  useEffect(() => {
    if (!withTeam || isLoading || teams.length === 0) return
    if (authStore.isAllTeams) return
    if (teams.some(candidate => candidate.id === authStore.activeTeamId)) return
    authStore.setActiveTeam(null)
  }, [withTeam, isLoading, teams, authStore.activeTeamId, authStore.isAllTeams, authStore])

  return {
    organizations,
    teams,
    org,
    team,
    setOrg: (orgId: number) => authStore.setActiveOrg(orgId),
    setTeam: (teamId: number | null) => authStore.setActiveTeam(teamId),
    isLoading
  }
}

export default useContextSwitcher
